import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { PatrolRound, PatrolRoundParams, ScanCheckpointDTO, ScanCheckpointResponse } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function usePatrolRounds(initialParams?: PatrolRoundParams) {
  const { logbookRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<PatrolRound> | null>(null);
  const [activeRound, setActiveRound] = useState<PatrolRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<PatrolRoundParams>(initialParams || { page: 1, limit: 20 });

  const fetchActiveRound = useCallback(async () => {
    try {
      const round = await logbookRepo.getActiveRound();
      setActiveRound(round);
    } catch {
      setActiveRound(null);
    }
  }, [logbookRepo]);

  const fetchRounds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await logbookRepo.getRounds(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar rondas');
    } finally {
      setLoading(false);
    }
  }, [logbookRepo, params]);

  useEffect(() => {
    fetchRounds();
    fetchActiveRound();
  }, [fetchRounds, fetchActiveRound]);

  const filterByStatus = useCallback((status: PatrolRoundParams['status']) => {
    setParams(prev => ({ ...prev, status, page: 1 }));
  }, []);

  const filterByDateRange = useCallback((startDate: string, endDate: string) => {
    setParams(prev => ({ ...prev, startDate, endDate, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const startRound = useCallback(async (notes?: string) => {
    try {
      const newRound = await logbookRepo.startRound(notes);
      setActiveRound(newRound);
      fetchRounds();
      return newRound;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al iniciar ronda');
    }
  }, [logbookRepo, fetchRounds]);

  const scanCheckpoint = useCallback(async (roundId: string, data: ScanCheckpointDTO): Promise<ScanCheckpointResponse> => {
    try {
      const response = await logbookRepo.scanCheckpoint(roundId, data);
      // Update active round with new data
      if (activeRound?.id === roundId) {
        setActiveRound(response.round);
      }
      return response;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al escanear punto de control');
    }
  }, [logbookRepo, activeRound]);

  const completeRound = useCallback(async (roundId: string, notes?: string) => {
    try {
      const completed = await logbookRepo.completeRound(roundId, notes);
      setActiveRound(null);
      fetchRounds();
      return completed;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al completar ronda');
    }
  }, [logbookRepo, fetchRounds]);

  const getRoundById = useCallback(async (id: string) => {
    try {
      return await logbookRepo.getRoundById(id);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al obtener ronda');
    }
  }, [logbookRepo]);

  return {
    rounds: data?.data || [],
    pagination: data?.meta,
    activeRound,
    // Alias for backward compatibility
    currentRound: activeRound,
    loading,
    error,
    params,
    filterByStatus,
    filterByDateRange,
    goToPage,
    startRound,
    scanCheckpoint,
    completeRound,
    getRoundById,
    refresh: fetchRounds,
    refreshActiveRound: fetchActiveRound,
  };
}
